import { useState } from "react";
import { Heart, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { dispatchCheckoutOpen, dispatchCheckoutClose } from "@/lib/checkoutOverlay";
import { trackInitiateCheckout, trackPurchase, getFbCookies } from "@/lib/analytics";
import { getAttribPayload } from "@/lib/attribution";
import { getSrcTrackingPayload } from "@/lib/srcTracking";
import { useToast } from "@/hooks/use-toast";

const QUICK_AMOUNTS = [100, 500, 1000, 2500];
const MIN_DONATION = 100;
const AMTM_LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_amtm-animal-care/artifacts/6ng7d8bg_amtm_logo_trim.png";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export default function DonateDialog({ open, onOpenChange, caseItem, fallbackLink, onSuccess }) {
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [donorPan, setDonorPan] = useState("");
  const [displayNamePublicly, setDisplayNamePublicly] = useState(true);
  const [residency, setResidency] = useState(null); // null | "yes" | "no"
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const effectiveAmount = customAmount ? parseFloat(customAmount) || 0 : amount;
  const customAmountTouched = customAmount !== "";
  const customAmountBelowMin = customAmountTouched && (parseFloat(customAmount) || 0) < MIN_DONATION;

  const handleDonate = async () => {
    if (effectiveAmount < MIN_DONATION) {
      toast({ variant: "destructive", title: `Minimum donation is ₹${MIN_DONATION}` });
      return;
    }
    if (!donorName.trim()) {
      toast({ variant: "destructive", title: "Full name is required" });
      return;
    }
    if (!EMAIL_REGEX.test(donorEmail.trim())) {
      toast({ variant: "destructive", title: "Enter a valid email address" });
      return;
    }
    if (donorPan && !PAN_REGEX.test(donorPan.trim())) {
      toast({ variant: "destructive", title: "PAN must be in the format ABCDE1234F" });
      return;
    }
    if (residency !== "yes") {
      toast({ variant: "destructive", title: "Please confirm your residency status" });
      return;
    }

    setLoading(true);
    toast({ title: "Preparing secure checkout..." });
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        if (fallbackLink) {
          window.open(fallbackLink, "_blank", "noopener,noreferrer");
          onOpenChange(false);
          return;
        }
        throw new Error("checkout-script-failed");
      }

      const { fbp, fbc } = getFbCookies();
      const { data: order } = await api.post("/razorpay/order", {
        case_id: caseItem.id,
        amount: effectiveAmount,
        donor_name: donorName.trim(),
        donor_email: donorEmail.trim(),
        donor_pan: donorPan.trim() || undefined,
        display_name_publicly: displayNamePublicly,
        is_indian_resident: residency === "yes",
        fbp: fbp || undefined,
        fbc: fbc || undefined,
        ...getAttribPayload(),
        ...getSrcTrackingPayload(),
      });

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Animal Matter To Me",
        description: `Treatment for ${caseItem.animal_name}`,
        image: AMTM_LOGO_URL,
        prefill: { name: donorName.trim(), email: donorEmail.trim() },
        theme: { color: "#F5A623" },
        handler: async (response) => {
          try {
            const { data } = await api.post("/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            trackPurchase({
              value: effectiveAmount,
              donationType: "case",
              caseId: caseItem.id,
              transactionId: response.razorpay_payment_id,
              contentName: caseItem.animal_name,
              contentCategory: "Case Donation",
              contentIds: [caseItem.id],
            });
            dispatchCheckoutClose();
            setLoading(false);
            onSuccess(data.case);
          } catch {
            dispatchCheckoutClose();
            setLoading(false);
            toast({
              variant: "destructive",
              title: "Could not confirm payment",
              description: "Please contact us if the amount was deducted.",
            });
          }
        },
        modal: {
          // Fires only when the donor closes Razorpay's overlay without
          // completing payment (cancel) - bring our dialog back so they
          // can retry without re-typing their details.
          ondismiss: () => {
            dispatchCheckoutClose();
            setLoading(false);
            onOpenChange(true);
          },
        },
      });
      rzp.on("payment.failed", () => {
        toast({ variant: "destructive", title: "Payment failed", description: "Please try again." });
      });

      // Close our own Radix dialog (releasing its focus trap) and hide any
      // other floating UI *before* Razorpay's overlay takes over - on
      // mobile Safari/Chrome a lingering focus trap or floating element
      // blocks taps/keyboard input inside Razorpay's iframe.
      onOpenChange(false);
      dispatchCheckoutOpen();
      trackInitiateCheckout({
        value: effectiveAmount,
        donationType: "case",
        caseId: caseItem.id,
        contentName: caseItem.animal_name,
        contentCategory: "Case Donation",
        contentIds: [caseItem.id],
      });
      rzp.open();
    } catch {
      toast({ variant: "destructive", title: "Could not start checkout", description: "Please try again." });
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="donate-dialog" className="rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Donate for {caseItem.animal_name} ❤</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {QUICK_AMOUNTS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => {
                  setAmount(a);
                  setCustomAmount("");
                }}
                data-testid={`donate-amount-${a}`}
                className={`h-12 rounded-xl border font-semibold text-sm sm:text-base transition-colors ${
                  !customAmount && amount === a
                    ? "bg-amtm-give text-amtm-giveText border-amtm-give"
                    : "border-amtm-border text-amtm-text hover:border-amtm-give"
                }`}
              >
                ₹{a.toLocaleString("en-IN")}
              </button>
            ))}
          </div>
          <div>
            <Label htmlFor="custom-amount">Or enter a custom amount (₹)</Label>
            <Input
              id="custom-amount"
              type="number"
              min={MIN_DONATION}
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder="e.g. 750"
              data-testid="donate-custom-amount-input"
              className={`mt-1.5 h-11 ${customAmountBelowMin ? "border-destructive focus-visible:ring-destructive" : ""}`}
            />
            {customAmountBelowMin && (
              <p className="text-xs text-destructive mt-1.5" data-testid="donate-custom-amount-min-error">
                Minimum donation is ₹{MIN_DONATION}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="donor-name">Full Name *</Label>
              <Input
                id="donor-name"
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                data-testid="donor-name-input"
                className="mt-1.5 h-11"
                required
              />
            </div>
            <div>
              <Label htmlFor="donor-email">Email *</Label>
              <Input
                id="donor-email"
                type="email"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                data-testid="donor-email-input"
                className="mt-1.5 h-11"
                required
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-amtm-border px-3.5 py-3">
            <Label htmlFor="display-publicly" className="cursor-pointer">
              Display my name publicly?
            </Label>
            <Switch
              id="display-publicly"
              checked={displayNamePublicly}
              onCheckedChange={setDisplayNamePublicly}
              data-testid="display-name-publicly-toggle"
            />
          </div>

          <div>
            <Label htmlFor="donor-pan">PAN (optional)</Label>
            <Input
              id="donor-pan"
              value={donorPan}
              onChange={(e) => setDonorPan(e.target.value.toUpperCase())}
              placeholder="ABCDE1234F"
              maxLength={10}
              data-testid="donor-pan-input"
              className="mt-1.5 h-11 uppercase"
            />
            <p className="text-xs text-amtm-textMuted mt-1.5">
              Optional — required only if you'd like an 80G tax-exemption certificate for this donation.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setResidency("yes")}
              data-testid="residency-yes"
              className={`h-11 rounded-xl border text-xs sm:text-sm font-medium px-2 transition-colors ${
                residency === "yes" ? "border-amtm-teal bg-amtm-teal/[0.06] text-amtm-text" : "border-amtm-border text-amtm-textMuted"
              }`}
            >
              I am an Indian resident / citizen
            </button>
            <button
              type="button"
              onClick={() => setResidency("no")}
              data-testid="residency-no"
              className={`h-11 rounded-xl border text-xs sm:text-sm font-medium px-2 transition-colors ${
                residency === "no" ? "border-destructive bg-destructive/[0.06] text-destructive" : "border-amtm-border text-amtm-textMuted"
              }`}
            >
              I'm not an Indian resident
            </button>
          </div>

          {residency === "no" && (
            <p className="text-xs text-amtm-textMuted bg-amtm-bg border border-amtm-border rounded-xl px-3.5 py-3" data-testid="fcra-message">
              Foreign contributions must be received through AMTM's designated FCRA account. Please write to{" "}
              <a href="mailto:getinvolved@amtmindia.org" className="text-amtm-giveText underline">
                getinvolved@amtmindia.org
              </a>{" "}
              and our team will help you give from outside India.
            </p>
          )}

          <p className="text-xs text-amtm-textMuted bg-amtm-bg border border-amtm-border rounded-xl px-3.5 py-3" data-testid="donate-80g-note">
            AMTM is 80G certified — your donation is tax-deductible. To receive your 80G certificate, email us at{" "}
            <a href="mailto:purvi@amtmindia.org" className="text-amtm-giveText underline">
              purvi@amtmindia.org
            </a>
            .
          </p>

          <Button
            onClick={handleDonate}
            disabled={loading || customAmountBelowMin || residency === "no"}
            className="w-full h-12 bg-amtm-give text-amtm-giveText hover:bg-amtm-giveHover font-semibold"
            data-testid="donate-dialog-submit-button"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Heart className="h-5 w-5 mr-2" /> Donate ₹{effectiveAmount.toLocaleString("en-IN")}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
